import React, { useMemo } from 'react';
import { Switch, Redirect } from 'react-router';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { SubNavigationBar } from '@common/components/navigation-bar';
import { useAppSelector } from '@common/hooks';
import { selected } from '@modules/organisation/selectors/organisation';
import Route, { PermissionRoute, getRedirect } from '../../../../common/components/route';
import Permission from '../../../../common/components/permission';
import RouteNotFound from '../../../../common/components/route-not-found';
import GeneralSettings from './general';
import TeamsSettings from './teams';
import FunctionsSettings from './functions';
import AdminsSettings from './admins';
import ComponentsSettings from './components';
import * as userSelector from '../../../core/selectors/logged-user';
import * as networkSelector from '../../selectors/network';
import { EComponentTypes, EPermissions } from '../../../../common/definitions';

const NetworkSettings = ({ network, permissions: allPermissions, t }) => {

  const organisation = useAppSelector(selected);

  const modulesVisible = useMemo(() => { // ONE-1036
    return (
      organisation.available_components.includes(EComponentTypes.SCHEDULE) ||
      organisation.available_components.includes(EComponentTypes.FLEXCHANGE)
    );
  }, [organisation]);

  const items = useMemo(() => {
    return [
      {
        permissions: {
          name: EPermissions.NETWORK_NETWORKS_UPDATE,
        },
        icon: 'settings',
        path: `/networks/${network.id}/settings/general`,
        title: t('network:settings_index_general_title'),
      }, {
        permissions: {
          name: EPermissions.NETWORK_TEAMS_VIEW,
        },
        icon: 'group',
        path: `/networks/${network.id}/settings/teams`,
        title: t('network:settings_index_teams_title'),
      }, {
        permissions: { // TODO
          name: EPermissions.NETWORK_USERS_UPDATE,
        },
        icon: 'assignment_ind',
        path: `/networks/${network.id}/settings/functions`,
        title: t('network:settings_index_functions_title'),
      }, {
        permissions: {
          name: EPermissions.NETWORK_NETWORK_ADMINS_VIEW,
        },
        icon: 'local_police',
        path: `/networks/${network.id}/settings/admins`,
        title: t('network:settings_index_admins_title'),
      }, (
        modulesVisible && {
          permissions: {
            name: EPermissions.NETWORK_NETWORKS_UPDATE_COMPONENTS,
          },
          icon: 'modules',
          path: `/networks/${network.id}/settings/components`,
          title: t('network:settings_index_modules_title'),
        }
      ),
    ].filter((slot) => !!slot);
  }, [t, modulesVisible]);

  const hasPermission = ({ name }) => allPermissions.includes(name); // eslint-disable-line react/prop-types
  const redirect = getRedirect(items, hasPermission);

  return (
    <>
      <SubNavigationBar>
        {items.map(({ permissions, ...item }) => (
          <Permission key={item.path} {...permissions}>
            <SubNavigationBar.Item {...item} />
          </Permission>
        ))}
      </SubNavigationBar>
      <Switch>
        <PermissionRoute
          path="/networks/:networkId/settings/general"
          component={GeneralSettings}
          permission={{ name: EPermissions.NETWORK_NETWORKS_UPDATE }}
        />
        <PermissionRoute
          path="/networks/:networkId/settings/teams"
          component={TeamsSettings}
          permission={{ name: EPermissions.NETWORK_TEAMS_VIEW }}
        />
        <PermissionRoute
          path="/networks/:networkId/settings/functions"
          component={FunctionsSettings}
          permission={{ name: EPermissions.NETWORK_USERS_UPDATE }}
        />
        <PermissionRoute
          path="/networks/:networkId/settings/admins"
          component={AdminsSettings}
          permission={{ name: EPermissions.NETWORK_NETWORK_ADMINS_VIEW }}
        />
        {
          modulesVisible && (
            <PermissionRoute
              path="/networks/:networkId/settings/components"
              component={ComponentsSettings}
              permission={{ name: EPermissions.NETWORK_NETWORKS_UPDATE_COMPONENTS }}
            />
          )
        }
        {redirect && <Redirect exact from="/networks/:networkId/settings" to={redirect} />}
        <Route component={RouteNotFound} />
      </Switch>
    </>
  );
};

const mapStateToProps = (state) => ({
  network: networkSelector.selected(state),
  permissions: userSelector.permissions(state),
});

export default connect(mapStateToProps)(withTranslation()(NetworkSettings));
